import { Helpers, Utils } from '../../Core';
import { getCmsDeliveryApi as API } from '../../Apis';
import { CmsCobrandedCardTypes as Internals } from '../../Types/internals';
import { CmsCobrandedCardTypes as Types } from '../../Types';
import { mocksService } from './GetCmsCobrandedCard.mocks';
import dataFields from '../../Types/CmsCobrandedCard.json';
import {
  dictionaryFactory,
  getAccessTokenFromBudle,
  parseStringToUpperCaseAndCompareIfIsTrue,
} from '../../Utils';

const bankTypeDictionary = dictionaryFactory({
  bb: 'BB',
  bradesco: 'BRADESCO',
  santander: 'SANTANDER',
}) as Record<string, Types.CobrandedBanksType>;

const cardTypeDictionary = dictionaryFactory({
  internacional: 'INTERNATIONAL',
  gold: 'GOLD',
  platinum: 'PLATINUM',
  infinite: 'INFINITE',
}) as Record<string, Types.CobrandedCardType>;

const milesStringToMilesNumber = (miles: string) => {
  return Number(miles.replace(/\./g, '').replace(/,/g, '.'));
};

const getMilesFromText = ({
  content,
}: Internals.ContentClubCard | Internals.ContentSmilesCard) => {
  if ('clube-conversao' in content) {
    return {
      milesOnAccrual: milesStringToMilesNumber(content['clube-milhas-pedir']),
      milesOnRenew: milesStringToMilesNumber(content['clube-milhas-renovacao']),
      milesPerDollar: milesStringToMilesNumber(content['clube-conversao']),
    };
  } else {
    return {
      milesOnAccrual: milesStringToMilesNumber(content['smiles-milhas-pedir']),
      milesOnRenew: milesStringToMilesNumber(
        content['smiles-milhas-renovacao'],
      ),
      milesPerDollar: milesStringToMilesNumber(content['smiles-conversao']),
    };
  }
};

const removeHtmlTagsAndBulletList = (htmlText: string) => {
  return htmlText.replace(/<\/?.*?>|•\s?|²/g, '');
};

const formatBenefits = (benefitsHTML: string): Types.BenefitType[] => {
  const titles = benefitsHTML
    .match(/<strong>(.+?)<\/strong>/g)
    ?.map(removeHtmlTagsAndBulletList);

  const contents = benefitsHTML
    .replace(/\n/g, '')
    .match(/<\/strong>(.*?)<strong>/g)
    ?.map((item) => item.split('<br>'))
    ?.map((item) => item.map(removeHtmlTagsAndBulletList));

  const lastContent = benefitsHTML
    .replace(/\n/g, '')
    .match(/<\/strong>(?!.*<\/strong>).+/g)
    ?.flatMap((item) => item.split('<br>'))
    ?.map(removeHtmlTagsAndBulletList);

  const result = [] as Types.BenefitType[];

  titles?.forEach((title, index) => {
    result.push({
      title,
      benefits: contents?.[index] ?? lastContent ?? [],
    });
  });

  return result;
};

const getBankLink = (
  bank: string,
  genericItem: Internals.CobrandedBankContent,
): string => {
  if (
    parseStringToUpperCaseAndCompareIfIsTrue(
      genericItem.content[`${bank}-is-logged-user`],
    )
  ) {
    const jsonAccessToken = getAccessTokenFromBudle();
    const token = jsonAccessToken.accessToken;
    return genericItem.content[`${bank}-link`] + `?aToken=${token}`;
  }

  return genericItem.content[`${bank}-link-external`];
};

const getCobrandedInfo = (
  content: Internals.CobrandedCmsContentInsideKey[],
): {
  cobrandedBanks: Types.CobrandedBank[];
  cobrandedCards: Types.CobrandedCard[];
} => {
  const cobrandedCards = [] as Types.CobrandedCard[];
  const cobrandedBanks = [] as Types.CobrandedBank[];
  const cardTitle = 'Temos o cartão de crédito GOL Smiles ideal para você';

  content.forEach((item) => {
    Promise.resolve()
      .then(() => {
        if ('cartao' in item) {
          cobrandedCards.push({
            type: cardTypeDictionary[item.tipo],
            image: item.cartao,
            title: cardTitle,
            text: item.cabecalho.content['cabecalho-texto'].replace(/¹/g, ''),
            askYoursText: item.cabecalho.content['cabecalho-botao'],
            club: getMilesFromText(item.clube),
            smiles: getMilesFromText(item.smiles),
            benefits: formatBenefits(
              item.beneficios.content['beneficios-texto'],
            ),
            importantText: item.importante.content['importante-texto'],
            termsText: item.termos.content['termos-texto'],
          });
        }

        for (const bank of Object.keys(item)) {
          const genericItem = item as Record<
            string,
            Internals.CobrandedBankContent
          >;

          const link = getBankLink(bank, genericItem[bank]);
          cobrandedBanks.push({
            type: bankTypeDictionary[bank],
            link,
            cellPhone: genericItem[bank].content[`${bank}-telefone`] ?? '',
          });
        }
      })
      .catch(() => null);
  });
  return { cobrandedCards, cobrandedBanks };
};

const handleSucessFinalResponseFunction = (
  data: Internals.CobrandedCmsData,
): Types.CobrandedCmsContent => {
  const content = Object.values(data.content.lista.content).map(
    (item) => item.content,
  );
  const cobrandedInfo = getCobrandedInfo(content);
  return {
    banks: cobrandedInfo.cobrandedBanks,
    cards: cobrandedInfo.cobrandedCards,
  };
};

const prepareCall = async () => {
  const { cmsDeliveryIds } = Helpers.getConstants();
  const { COBRANDED_CARDS } = cmsDeliveryIds as { [key: string]: string };

  return API.call.get({
    contentId: COBRANDED_CARDS,
  });
};

export const { machine, Docs } = Utils.genericMachine({
  mocksService,
  dataFields,
  definitionsAPI: API.definitions,
  invokeApiFunction: prepareCall,
  handleResponse: {
    handleSucessFinalResponseFunction,
  },
});
